<template>
  <LayoutDefault class="launch">
    <div class="loader-wrapper auth">
      <img src="@/assets/svg/loading.svg" class="loader" />
      <Logo />
      <div class="error-message" v-if="errorMessage || launchError">
        {{ errorMessage || launchError }}
      </div>
    </div>
  </LayoutDefault>
</template>

<script>
import {
  ROUTE_NAME,
  API_URLS,
  ARENA_GAME_TYPE,
  CLIENT_VERSION,
  DESKTOP_PANES_STORAGE_KEY,
  TAI_XIU_FOCUS,
  getGameRouteName,
  resolveClientVersion,
} from '@/utils/constant';
import { getData, postData, keysToCamel, isIframeReload, getItemFromStore, getDeviceId, getDeviceFingerprint } from '@/utils/helpers';
import { initMgConnection, initMainConnection } from '@/utils/websocket';
import { mapActions, mapGetters } from 'vuex';
import LayoutDefault from '@/layouts/default.vue';
import Logo from '@/components/logo.vue';

export default {
  components: {
    LayoutDefault,
    Logo,
  },
  data: () => {
    return {
      launchError: '',
    };
  },
  async mounted() {
    try {
      const query = this.$route.query;
      // `v=2` opts into the Tai Xiu client on any device; `focus=tx` does too when a Tai Xiu
      // arena is open. Whether v2 renders the split view or the mobile stack is decided
      // downstream by viewport.
      const versionParam = query.v;
      let launchClientVersion = resolveClientVersion(versionParam);
      const lastIframeUrl = getItemFromStore('general.general.lastIframeUrl');
      if (!isIframeReload(lastIframeUrl)) {
        this.resetGeneral();
        const { auth, token, theme, member_token, lang, streamv, arena, focus, pip, lobby_url, binary } = query;
        let res;
        let serverTimeDiff = 0;
        let params;
        const deviceId = getDeviceId();
        const deviceFingerprint = await getDeviceFingerprint();
        if (member_token) {
          params = {
            access_token: member_token,
            device_id: deviceId,
            device_fp: deviceFingerprint,
          };
          res = await postData(API_URLS.CHECK_AUTH_WITH_TOKEN, '', params);
          serverTimeDiff = res.serverTimeDiff;
        } else {
          if (!auth) {
            this.$router.push({ name: ROUTE_NAME.notFound });
            return;
          }
          params = {
            id: auth,
            token,
            device_id: deviceId,
            device_fp: deviceFingerprint,
          };
          res = await postData(API_URLS.CHECK_AUTH, '', params, true);
          serverTimeDiff = res.serverTimeDiff;
        }
        if (theme) {
          res.theme = theme;
        }
        if (lang && res.config.language_list.includes(lang)) {
          res.config.language = lang;
        }
        // Default to streamv=3 (VePlayer) when the launch URL has no / invalid streamv.
        res.streamVersion = Number(streamv) || 3;
        if (parent !== window) {
          res.lastIframeUrl = document.location.href;
        }
        // Store the server time difference for timezone validation
        if (serverTimeDiff) {
          res.serverTimeDiff = serverTimeDiff;
        }
        if (arena) {
          res.selectedArenaCode = arena;
        }

        if (focus) {
          res.focus = focus;
        }

        if (pip === 'false') {
          res.pipDisabled = true;
        } else if (pip === 'true') {
          res.pipDisabled = false;
        }

        if (lobby_url) {
          this.setLobbyUrl(lobby_url);
        } else {
          this.setLobbyUrl('');
        }
        if (binary === 'false') {
          res.binaryDisabled = true;
        } else if (binary === 'true') {
          res.binaryDisabled = false;
        }

        // `focus=tx` prefers a live Tài Xỉu arena, whichever client `v` chose: v1 moves to the
        // Tài Xỉu client when one is open; with none open, the launch stays where `v` put it.
        if (focus === TAI_XIU_FOCUS) {
          if (
            launchClientVersion !== CLIENT_VERSION.taiXiu &&
            (await this.hasTaiXiuArenas(res.token, res.config.language))
          ) {
            launchClientVersion = CLIENT_VERSION.taiXiu;
          }
          if (launchClientVersion === CLIENT_VERSION.taiXiu) {
            // A fresh launch opens on Tài Xỉu, not on the panes a previous launch
            // in this tab left behind.
            try {
              window.sessionStorage.removeItem(DESKTOP_PANES_STORAGE_KEY);
            } catch (e) {
              // Blocked storage: nothing was restored from it either.
            }
          }
        }
        const clientVersion = launchClientVersion;
        this.updateGeneral({
          ...keysToCamel(res),
          clientVersion,
        });
        this.$i18n.set(res.config.language || 'en');
        this.updateActiveChip(Number(res.config.bet_chips[0]));
        initMgConnection(res.token, res.config.odds_type);
        // Pre-establish main WebSocket so stream URL requests don't wait for handshake
        initMainConnection(res.token);
      } else {
        if (versionParam !== undefined && versionParam !== '') {
          launchClientVersion = resolveClientVersion(versionParam);
          this.updateGeneral({ clientVersion: launchClientVersion });
        } else {
          // No `v` on an iframe reload: keep whatever the session launched with.
          launchClientVersion = this.clientVersion;
        }
        this.$i18n.set(this.config.language || 'en');
        this.updateActiveChip(Number(this.config.betChips[0]));
        initMgConnection(this.token, this.config.oddsType);
        // Pre-establish main WebSocket so stream URL requests don't wait for handshake
        initMainConnection(this.token);
      }
      this.getOwnerTickets({
        isSeen: false,
      });
      this.$router.push({ name: getGameRouteName(launchClientVersion) });
    } catch (error) {
      if (error && error._errorGroup !== undefined) {
        // API error with structured error group/code from interceptor
        // Group 2 = backend error, Group 3 = network/infrastructure error
        this.launchError = `${error._errorGroup}-${error._errorCode}`;
      } else {
        // Frontend JavaScript error (group 1, code 0)
        this.launchError = '1-0';
      }
    }
  },
  computed: {
    ...mapGetters('general', ['errorHandler', 'config', 'token', 'clientVersion']),
    errorMessage() {
      if (this.errorHandler.errorMessage === 'disabled account') {
        return this.$t('Account disabled');
      }
      return '';
    },
  },
  methods: {
    ...mapActions('general', ['updateGeneral', 'resetGeneral', 'setLobbyUrl']),
    ...mapActions('bets', ['updateActiveChip']),
    ...mapActions('jackpot', ['getOwnerTickets']),
    // Same query as taiXiu/getArenas. A failed lookup counts as none open.
    async hasTaiXiuArenas(token, language) {
      try {
        const params = new URLSearchParams({
          has_active_match_day: 'true',
          lang: language || 'en',
          game_type: String(ARENA_GAME_TYPE.taiXiu),
        });
        const arenas = await getData(`${API_URLS.ARENAS}?${params.toString()}`, token);
        return Array.isArray(arenas) && arenas.length > 0;
      } catch (e) {
        return false;
      }
    },
  },
};
</script>

<style lang="scss">
.launch {
  background-color: #000;
  @include fullHeightWidth;
  text-align: center;

  .loader-wrapper {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    font-size: 14px;
    display: flex;
    flex-direction: column;
    @include alignJustifyCenter;

    .loader {
      width: 68px;
    }
    .logo {
      width: 104px;
      height: 34px;
      margin: 16px 0 0;
      object-fit: contain;
    }
    .error-message {
      font-size: 20px;
      color: #fff;
      margin-top: 16px;
      text-transform: uppercase;
    }
  }
}
</style>
